import {USER_DATA} from 'src/app/_interfaces/auth.interface'
import {UserSubscriptionPlan} from 'src/interfaces/Platform.interface'
import {isActiveFreeTrial28DaySubscription} from 'src/utils/freeTrialDeferredOnboarding'

/** Shared plan list helpers for Plan & Usage (exclude generic Free, keep Free Trial). */

export const isFreeTrialPlanRecord = (plan: {
  display_name?: string
  plan_name?: string
  billing_frequency?: string
  plan_type?: string
}): boolean => {
  const planDisplayName = String(plan?.display_name || plan?.plan_name || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
  return (
    plan?.billing_frequency === 'FREE_FOREVER' &&
    plan?.plan_type === 'COMMON' &&
    planDisplayName.startsWith('free trial - 28 days')
  )
}

/** Split API display name like "Free Trial - 28 Days (No CC required)" for reference-style title row. */
export const splitFreeTrialPlanDisplayTitle = (
  displayName?: string | null
): {primary: string; secondary: string | null} => {
  const trimmed = String(displayName ?? '').replace(/\s+/g, ' ').trim()
  if (!trimmed) {
    return {primary: '', secondary: null}
  }

  const dashMatch = /^free trial\s*-\s*(.+)$/i.exec(trimmed)
  if (dashMatch?.[1]) {
    return {primary: 'Free Trial', secondary: `· ${dashMatch[1]}`}
  }

  const dotMatch = /^free trial\s*·\s*(.+)$/i.exec(trimmed)
  if (dotMatch?.[1]) {
    return {primary: 'Free Trial', secondary: `· ${dotMatch[1]}`}
  }

  return {primary: trimmed, secondary: null}
}

/**
 * Subscription ID from GET /api/activate-billing-plan/ (`UserSubscriptionPlan`).
 * Priority: current_subscription_id → subscription_id → user_transactions.subscription_id
 */
export const getActivateBillingPlanSubscriptionId = (
  plan?: {
    current_subscription_id?: string | null
    subscription_id?: string | null
    user_transactions?: {subscription_id?: string | null} | null
  } | null
): string | null => {
  const candidates = [
    plan?.current_subscription_id,
    plan?.subscription_id,
    plan?.user_transactions?.subscription_id,
  ]
  for (const value of candidates) {
    const trimmed = String(value ?? '').trim()
    if (trimmed && trimmed !== '\n') {
      return trimmed
    }
  }
  return null
}

const PAYPAL_PLAN_ID_PATTERN = /^P-[A-Z0-9]+$/i

const isSerializedJsonPlanDescription = (trimmed: string): boolean => {
  if (trimmed === '[]' || trimmed === '{}') {
    return true
  }
  const first = trimmed.charAt(0)
  if (first !== '[' && first !== '{') {
    return false
  }
  try {
    const parsed = JSON.parse(trimmed) as unknown
    return typeof parsed === 'object' && parsed !== null
  } catch {
    return false
  }
}

/**
 * Plan & Usage card subtitle only. Rejects empty values, billing flags, and
 * identifiers such as PayPal `plan_id` / numeric `id` stored in `description`.
 */
export const isMeaningfulPlanDescription = (
  description?: string | null,
  plan?: {plan_id?: string | null; id?: number | string | null} | null
): boolean => {
  const trimmed = String(description ?? '')
    .replace(/\s+/g, ' ')
    .trim()
  if (!trimmed) return false
  if (trimmed === 'BILLED_MANUALLY') return false
  if (isSerializedJsonPlanDescription(trimmed)) return false
  if (PAYPAL_PLAN_ID_PATTERN.test(trimmed.replace(/\s+/g, ''))) return false
  if (plan?.plan_id && trimmed === String(plan.plan_id).trim()) return false
  if (plan?.id != null && trimmed === String(plan.id).trim()) return false
  return true
}

export const isGenericFreePlanRecord = (plan: {
  display_name?: string
  plan_name?: string
  billing_frequency?: string
  plan_type?: string
  price?: {value?: number; type?: string}
}): boolean => {
  if (plan?.price?.type === 'CUSTOM_PLAN') {
    return false
  }
  const isFree =
    plan?.billing_frequency === 'FREE_FOREVER' || Number(plan?.price?.value) === 0
  return isFree && !isFreeTrialPlanRecord(plan)
}

/** Fallback rows rendered when `billingplanfeature_set` is empty. */
export const FALLBACK_PLAN_FEATURE_COUNT = 8

/**
 * Collapsed Plan & Usage feature rows. Tuned to the current 14px / 10px-gap
 * feature rows rather than the standalone HTML sample (`COLLAPSED = 6` there
 * used different spacing).
 */
export const PLAN_CARD_COLLAPSED_FEATURE_COUNT = 5

/** Plan & Usage feature-section heading. Uses the existing parent-plan name when present. */
export const getPlanCardFeatureHeading = (parentPlanName?: string | null): string => {
  const parent = String(parentPlanName ?? '').trim()
  if (!parent) {
    return 'Core features'
  }
  return `Everything in ${parent}, and:`
}

/** Count of feature rows actually rendered on a plan card. Excludes the parent-plan badge. */
export const getPlanCardFeatureCount = (plan?: {
  billingplanfeature_set?: unknown[] | null
} | null): number => {
  const features = plan?.billingplanfeature_set
  if (Array.isArray(features) && features.length > 0) {
    return features.length
  }
  return FALLBACK_PLAN_FEATURE_COUNT
}

/**
 * Recommended / featured plan for the Plan & Usage "Most popular" badge.
 * No confirmed API field exists in the typed frontend model; this only
 * activates if the billing-plan payload already includes a boolean flag.
 */
export const isRecommendedBillingPlan = (plan?: {
  is_recommended?: boolean
  isRecommended?: boolean
} | null): boolean => plan?.is_recommended === true || plan?.isRecommended === true

/**
 * Billing-only welcome user (Plan & Usage UI).
 * Same 28-day Free Trial onboarding cohort as deferred flow, but does NOT require phone step.
 * Does not alter global deferred onboarding semantics.
 */
export const isPlanAndUsageWelcomeUser = (
  account: USER_DATA | null | undefined,
  subscription: UserSubscriptionPlan | null | undefined
): boolean => {
  if (!account) {
    return false
  }
  if (account.user_type !== 'USER') {
    return false
  }
  if (account.isOnboardingProcessCompleted === true) {
    return false
  }
  if (!account.is_subscribed) {
    return false
  }
  if (account.is_platform_choice_done === true) {
    return false
  }
  return isActiveFreeTrial28DaySubscription(subscription)
}
